import { orderBy, cloneDeep } from 'lodash';
import { Component, Inject, Input, OnInit, Output, TemplateRef, ViewChild, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription, catchError, throwError } from 'rxjs';
import { AssessmentReportsService, RdSAPAssessmentReportMimeTypes, RdSAPGeneralDetailUIDTOV0, RdSAPResultsDetailUIDTOV0, ReportStatus } from 'src/rdsap-api';
import { AGGREGATED_DATA_SERVICE_TOKEN, IAggregatedDataService } from '../../../models/data/aggregated-data.interface';
import { UtilsService } from '../../../services/utils/utils.service';
import { RatingColorClasses, RatingColorService, RatingDetails } from '../../../services/rating/rating.service';
import { RdSAPSuggestedImprovementV0 } from '../../../models/rdsap/rdSAPSuggestedImprovementV0';
import { ToastService } from '../../../services/toast/toast.service';
import { environment } from '../../../../environments/environment';
import { RdSAPAssessmentReport } from '../../../../core-state/services/data/rdsap-survey-details-data.service';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';


@Component({
  selector: 'app-rdsap-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss']
})
export class SummaryComponent implements OnInit {
  @Input() isEdit = false;
  @Input() canCalculate = false;
  @Output() addendaData = new EventEmitter<any>();
  @Output() deselectedItemsData = new EventEmitter<any>();
  @Input() getAddendum: any;

  rdSAPDetails$: Observable<RdSAPAssessmentReport[]>;
  private subscriptions = new Subscription();
  loading: boolean;
  isLoading = false;
  details: RdSAPAssessmentReport;
  eeStatusDetails: RdSAPResultsDetailUIDTOV0;
  eeGeneralDetails: RdSAPGeneralDetailUIDTOV0 = {
    address: undefined
  };
  selectedRdSapRatingInfo = [
    {
      description: 'EE Current',
      color: 'bg-grey',
      icon: 'assessment',
      rating: '0'
    },
    {
      description: 'EE Potential',
      color: 'bg-grey',
      icon: 'assessment',
      rating: '0'
    },
    {
      description: 'EI Current',
      color: 'bg-grey',
      icon: 'assessment',
      rating: '0'
    },
    {
      description: 'EI Potential',
      color: 'bg-grey',
      icon: 'assessment',
      rating: '0'
    }
  ];


  successMessage = '';
  standardMessage = '';
  errorMessage = '';

  @ViewChild('standardTpl', { static: true }) standardTpl!: TemplateRef<any>;
  @ViewChild('successTpl', { static: true }) successTpl!: TemplateRef<any>;
  @ViewChild('dangerTpl', { static: true }) dangerTpl!: TemplateRef<any>;

  activeRdSapRatingInfo: any[] = [...this.selectedRdSapRatingInfo];

  eeRecommendations: RdSAPSuggestedImprovementV0[];
  addressId: any;
  reportReferenceNumber: any;

  get showCalculateButton() {
    const statuses = [ReportStatus.InProgress, ReportStatus.Calculated, ReportStatus.Completed]
    return statuses.includes(this.details?.reportStatus);
  }

  get showEpcPreviewButton() {
    const statuses = [ReportStatus.Calculated, ReportStatus.Completed]
    return statuses.includes(this.details?.reportStatus);
  }

  get showLodgeButton() {
    const statuses = [ReportStatus.Completed]
    return statuses.includes(this.details?.reportStatus);
  }

  get showSapWorksheetButton() {
    const statuses = [ReportStatus.Calculated, ReportStatus.Lodged, ReportStatus.Completed]
    return statuses.includes(this.details?.reportStatus);
  }

  get showOpenEPCButton() {
    const statuses = [ReportStatus.Lodged]
    return statuses.includes(this.details?.reportStatus);
  }

  get sortedEERecommendations() {
    return orderBy(this.eeRecommendations, ['sequence'], ['asc']);
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private ratingColorService: RatingColorService,
    private utilsService: UtilsService,
    private assessmentService: AssessmentReportsService,
    private toastService: ToastService,
    private cookieService: CookieService,
    @Inject(AGGREGATED_DATA_SERVICE_TOKEN) private aggregatedDataService: IAggregatedDataService,
  ) {
  }

  ngOnInit() {
    this.rdSAPDetails$ = this.aggregatedDataService.rdSAPDetails$;
    this.getAndSetRdSAPDetails();
  }

  getAndSetRdSAPDetails() {
    this.loading = true;
    this.rdSAPDetails$.pipe(
      catchError(error => {
        console.error('An error occurred:', error);
        this.loading = false;
        return throwError(() => error);
      })
    ).subscribe((data) => {
      if (!data || !data.length) {
        this.loading = false;
        return;
      }
      this.details = data[0];
      this.setActiveDataSetRatingInfo(this.details.results as RdSAPResultsDetailUIDTOV0);
      this.eeStatusDetails = this.details.results || {};
      this.eeGeneralDetails.addendum = this.details.general?.addendum || {};
      this.eeRecommendations = this.details.results.recommendations;
    })
  }

  showStandard(msg: string) {
    this.standardMessage = msg;
    this.toastService.show({ template: this.standardTpl });
  }

  showSuccess(msg: string) {
    this.successMessage = msg;
    this.toastService.show({ template: this.successTpl, classname: 'bg-success text-light', delay: 10000 });
  }

  showDanger(msg: string) {
    this.errorMessage = msg;
    this.toastService.show({ template: this.dangerTpl, classname: 'bg-danger text-light', delay: 15000 });
  }

  setActiveDataSetRatingInfo(dataSet: RdSAPResultsDetailUIDTOV0) {
    const ratings: RatingDetails = {
      sapRating: dataSet.sapRating,
      potentialSAPRating: dataSet.potentialSAP,
      eiRating: dataSet.eiRating,
      potentialEIRating: dataSet.potentialEI
    };
    const ratingClasses = this.getRatingColorClasses(ratings);
    this.activeRdSapRatingInfo[0].rating = dataSet.sapRating;
    this.activeRdSapRatingInfo[0].color = ratingClasses.sapRatingColorClass;
    this.activeRdSapRatingInfo[1].rating = dataSet.potentialSAP;
    this.activeRdSapRatingInfo[1].color = ratingClasses.potentialSAPRatingColorClass;
    this.activeRdSapRatingInfo[2].rating = dataSet.eiRating;
    this.activeRdSapRatingInfo[2].color = ratingClasses.eiRatingColorClass;
    this.activeRdSapRatingInfo[3].rating = dataSet.potentialEI;
    this.activeRdSapRatingInfo[3].color = ratingClasses.potentialEIColorClass;
  }

  getRatingColorClasses(asset: RatingDetails): RatingColorClasses {
    return this.ratingColorService.getColorClasses(asset);
  }

  calculate() {
    this.isLoading = true;
    this.showStandard('Running calculation');
    this.assessmentService.newUpdateAssessmentStatus(
      this.details?.generic?.reportReferenceNumber,
      'CALCULATE'
    )
      .pipe(
        catchError((error) => {
          this.refreshReportState();
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          this.refreshReportState();
        },
        error: (err) => {
          this.showDanger('Error running calculation');
          console.log(err);
        },
        complete: () => {
          this.refreshReportState();
          this.showSuccess('Calculated successfully');
        }
      });
  }

  private refreshReportState() {
    this.aggregatedDataService.loadRdSAPDetailsForAsset({
      quid: 'QUID202904',
      reportReferenceNumber: this.details?.generic?.reportReferenceNumber,
      accept: environment.accept as RdSAPAssessmentReportMimeTypes
    });
  }

  getButtonText(): string {
    if (this.details) {
      const status = this.details.reportStatus;

      if (status === ReportStatus.Completed) {
        return 'Open Preview';
      } else if (status === ReportStatus.Calculated) {
        return 'EPC Preview';
      }
    }
    return 'EPC Preview';
  }

  preview() {
    if (this.details?.reportStatus === ReportStatus.Completed) {
      this.openLinkInNewTab(`${environment.previewBaseUrl}${this.details?.generic?.reportReferenceNumber}`)
      return;
    }
    this.showStandard('Completing report');
    this.assessmentService.newUpdateAssessmentStatus(this.details?.generic?.reportReferenceNumber, 'COMPLETE')
      .pipe(
        catchError((error) => {
          this.refreshReportState();
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res) => {
          if(res) {
            this.openLinkInNewTab(`${environment.previewBaseUrl}${this.details?.generic?.reportReferenceNumber}`);
            this.showSuccess('Completed successfully');
          } else {
            this.showDanger('Error completing report');
          }
          this.refreshReportState();
        },
        complete: () => {
          this.showSuccess('Completed successfully');
          this.refreshReportState();
        }
      });
  }

  lodge() {
    this.showStandard('Lodging report');
    this.assessmentService.newUpdateAssessmentStatus(
      this.details?.generic?.reportReferenceNumber,
      'LODGE'
    )
      .pipe(
        catchError((error) => {
          if (error?.status === 402) {
            this.showDanger('Your credit balance is insufficient');
          } else {
            this.showDanger('An unexpected error occurred');
          }
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res) => {
          if (res) {
            this.showSuccess('Lodged successfully');
          } else {
            this.showDanger('Error lodging report');
          }
          this.refreshReportState();
        },
        complete: () => {
          this.showSuccess('Lodged successfully');
          this.refreshReportState();
        }
      });
  }
  

  downloadPdf() {
    const reportReferenceNumber = this.details?.generic?.reportReferenceNumber;
    
    if (!reportReferenceNumber) {
      console.error('Report Reference Number is not available.');
      return;
    }
    const url = `${environment.baseApiUrl}/v1/report/${reportReferenceNumber}/document`
    const authToken = this.cookieService.get('authToken');
    const apiKey = this.cookieService.get('apiKey');
  
    if (!authToken || !apiKey) {
      console.error('Authentication tokens are not available.');
      return;
    }
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + authToken,
      'x-api-key': apiKey,
      'Accept': 'application/json, application/pdf+SAP-worksheet',
    });
  
    this.http.get(url, { headers: headers, responseType: 'blob' }).subscribe(
      (response: Blob) => {
        const blobUrl = window.URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `assessment_report_${reportReferenceNumber}.pdf`; 
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      },
      (error: HttpErrorResponse) => {
      }
    );
  }

  getEPC() {
    this.openLinkInNewTab(`${environment.reportBaseUrl}${this.details?.generic?.reportReferenceNumber}`)
  }

  openLinkInNewTab(url: string) {
    window.open(url, '_blank');
  }

  setAddendum(value, event) {
    this.eeGeneralDetails = cloneDeep(value);
    if (event.addendumNumbers) {
      this.eeGeneralDetails.addendum.addendumNumbers = event.addendumNumbers;
    }
    this.eeGeneralDetails.addendum.accessIssues = event.accessIssues;
    this.eeGeneralDetails.addendum.narrowCavities = event.narrowCavities;
    this.eeGeneralDetails.addendum.highExposure = event.highExposure;
    this.addendaData.emit(this.eeGeneralDetails);
  }


  setDeselectedItems(value, event) {
    this.eeGeneralDetails = cloneDeep(value);
    this.eeGeneralDetails.deselectedImprovementMeasures = event;
    this.deselectedItemsData.emit(this.eeGeneralDetails)
    console.log('Updated General Details:', this.eeGeneralDetails);
  }
}
